import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowPathIcon,
  EyeIcon,
  PlusIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import { paperTradingApi, exchangeApi } from '@/services/api';
import { formatCurrency, formatPercentage, formatCryptoPrice, formatCryptoQuantity, formatCryptoAmount } from '@/utils/formatters';
import LoadingSpinner from '@/components/UI/LoadingSpinner';
import SummaryCard from '@/components/UI/SummaryCard';
import AllocationChart from '@/components/Charts/AllocationChart';
import PerformanceChart from '@/components/Charts/PerformanceChart';

interface Position {
  symbol: string;
  quantity: number;
  averagePrice: number;
  currentPrice: number;
  marketValue: number;
  unrealizedPnL: number;
  unrealizedPnLPercentage: number;
  allocation: number;
}

interface Portfolio {
  totalValue: number;
  cashAvailable: number;
  totalInvested: number;
  totalReturn: number;
  totalReturnPercentage: number;
  dayChange: number;
  dayChangePercentage: number;
  positions: Position[];
  allocation: Array<{
    symbol: string;
    value: number;
    percentage: number;
    target?: number;
    deviation?: number;
  }>;
}

interface PortfolioPerformance {
  period: string;
  returns: Array<{ date: string; value: number }>;
  metrics: {
    totalReturn: number;
    annualizedReturn: number;
    volatility: number;
    sharpeRatio: number;
    maxDrawdown: number;
    winRate: number;
  };
}

const PortfolioPage: React.FC = () => {
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [performance, setPerformance] = useState<PortfolioPerformance | null>(null);
  const [liveBalance, setLiveBalance] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState('1M');
  const [refreshing, setRefreshing] = useState(false);

  const fetchPortfolioData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Calculate date range based on selected period
      const endDate = new Date().toISOString();
      let startDate: string;
      
      switch (selectedPeriod) {
        case '1D':
          startDate = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
          break;
        case '1W':
          startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
          break;
        case '1M':
          startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
          break;
        case '3M':
          startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();
          break;
        case '6M':
          startDate = new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString();
          break;
        case '1Y':
        default:
          startDate = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString();
          break;
      }

      const [portfolioRes, performanceRes, liveBalanceRes] = await Promise.allSettled([
        paperTradingApi.getPortfolio(),
        paperTradingApi.getPerformance({ dateFrom: startDate, dateTo: endDate }),
        exchangeApi.getAggregatedBalance({ includeZero: true })
      ]);

      if (portfolioRes.status === 'fulfilled') {
        setPortfolio((portfolioRes.value as any).data?.portfolio);
      } else {
        console.warn('Portfolio fetch failed:', portfolioRes.reason);
      }

      if (performanceRes.status === 'fulfilled') {
        // Performance data should have the correct structure now
        const perfData = (performanceRes.value as any).data;
        setPerformance(perfData);
      } else {
        console.warn('Performance fetch failed:', performanceRes.reason);
      }

      if (liveBalanceRes.status === 'fulfilled') {
        setLiveBalance((liveBalanceRes.value as any).data);
      } else {
        console.warn('Live balance fetch failed:', liveBalanceRes.reason);
      }

      // If both failed, show error
      if (portfolioRes.status === 'rejected' && performanceRes.status === 'rejected' && liveBalanceRes.status === 'rejected') {
        throw new Error('Failed to load portfolio data');
      }

    } catch (error: any) {
      console.error('Portfolio fetch error:', error);
      setError(error.message || 'Failed to load portfolio data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPortfolioData();
  }, [selectedPeriod]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPortfolioData();
    setRefreshing(false);
    toast.success('Portfolio data refreshed');
  };

  if (loading && !portfolio) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error && !portfolio) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <ChartBarIcon className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Unable to load portfolio</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-display font-bold text-primary-custom">Portfolio Overview</h1>
          <p className="text-body text-secondary-custom">Monitor your investments and performance</p>
        </div>
        <div className="flex items-center space-x-3">
          {/* Period selector */}
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="1D">1 Day</option>
            <option value="1W">1 Week</option>
            <option value="1M">1 Month</option>
            <option value="3M">3 Months</option>
            <option value="6M">6 Months</option>
            <option value="1Y">1 Year</option>
          </select>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn btn-primary"
          >
            {refreshing ? (
              <LoadingSpinner size="sm" className="mr-2" />
            ) : (
              <ArrowPathIcon className="h-4 w-4 mr-2" />
            )}
            Refresh
          </button>
        </div>
      </div>

      {/* Live Exchange Balances */}
      {liveBalance && (
        <div className="mb-6">
          <h2 className="text-heading-2 font-semibold text-primary-custom mb-4">Live Exchange Balances</h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {liveBalance.aggregated && Object.keys(liveBalance.aggregated).length > 0 ? (
              Object.entries(liveBalance.aggregated).map(([currency, data]: [string, any]) => (
                <SummaryCard
                  key={currency}
                  title={`${currency} Available`}
                  value={formatCurrency(data.totalBalance, currency === 'USD' ? '$' : '')}
                  change={0}
                  icon={CurrencyDollarIcon}
                  trend="neutral"
                />
              ))
            ) : (
              <div className="col-span-full">
                <div className="card bg-white dark:bg-gray-800 p-6 text-center">
                  <CurrencyDollarIcon className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                  <h3 className="text-heading-3 text-primary-custom dark:text-white mb-2">No Live Exchange Funds</h3>
                  <p className="text-body text-secondary-custom dark:text-gray-400">
                    Your Kraken account shows zero balances. Fund your exchange account to start live trading.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Paper Trading Portfolio Summary */}
      <div>
        <h2 className="text-heading-2 font-semibold text-primary-custom mb-4">Paper Trading Portfolio</h2>
        {portfolio && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <SummaryCard
              title="Total Portfolio Value"
              value={formatCurrency(portfolio.totalValue)}
              icon={CurrencyDollarIcon}
              trend={portfolio.dayChangePercentage > 0 ? 'up' : 'down'}
              change={portfolio.dayChangePercentage}
              changeType="percentage"
              loading={loading}
            />
            
            <SummaryCard
              title="Total Return"
              value={formatCurrency(portfolio.totalReturn)}
              icon={ArrowTrendingUpIcon}
              trend={portfolio.totalReturnPercentage > 0 ? 'up' : 'down'}
              change={portfolio.totalReturnPercentage}
              changeType="percentage"
              loading={loading}
            />
            
            <SummaryCard
              title="Day Change"
              value={formatCurrency(portfolio.dayChange)}
              icon={portfolio.dayChange >= 0 ? ArrowTrendingUpIcon : ArrowTrendingDownIcon}
              trend={portfolio.dayChange >= 0 ? 'up' : 'down'}
              change={portfolio.dayChangePercentage}
              changeType="percentage"
              loading={loading}
            />
            
            <SummaryCard
              title="Cash Available"
              value={formatCurrency(portfolio.cashAvailable)}
              icon={CurrencyDollarIcon}
              loading={loading}
            />
          </div>
        )}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Chart */}
        {performance && performance.returns && Array.isArray(performance.returns) && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Performance</h3>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {performance.metrics && formatPercentage(performance.metrics.totalReturn)} return
              </div>
            </div>
            <PerformanceChart data={performance.returns} />
          </div>
        )}

        {/* Allocation Chart */}
        {portfolio && portfolio.allocation && Array.isArray(portfolio.allocation) && portfolio.allocation.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Asset Allocation</h3>
              <button className="text-sm text-indigo-600 hover:text-indigo-800">
                Rebalance
              </button>
            </div>
            <AllocationChart data={portfolio.allocation} />
          </div>
        )}
      </div>

      {/* Positions Table */}
      {portfolio && portfolio.positions && Array.isArray(portfolio.positions) && portfolio.positions.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Current Positions</h3>
              <button className="flex items-center text-sm text-indigo-600 hover:text-indigo-800">
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Position
              </button>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Asset
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Avg Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Current Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Market Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    P&L
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Allocation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {portfolio.positions.map((position, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {position.symbol}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatCryptoQuantity(position.quantity)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatCryptoPrice(position.averagePrice)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatCryptoPrice(position.currentPrice)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatCryptoAmount(position.marketValue)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className={`text-sm font-medium ${
                          position.unrealizedPnL >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {formatCryptoAmount(position.unrealizedPnL)}
                        </span>
                        <span className={`text-xs ml-1 ${
                          position.unrealizedPnL >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          ({formatPercentage(position.unrealizedPnLPercentage)})
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 dark:bg-gray-600 rounded-full h-2 mr-2">
                          <div
                            className="bg-indigo-600 h-2 rounded-full"
                            style={{ width: `${Math.min(position.allocation, 100)}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-900 dark:text-white">{position.allocation}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button className="text-indigo-600 hover:text-indigo-900">
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button className="text-indigo-600 hover:text-indigo-900">
                          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty state for positions */}
      {portfolio && (!portfolio.positions || !Array.isArray(portfolio.positions) || portfolio.positions.length === 0) && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8">
          <div className="text-center">
            <ChartBarIcon className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No positions yet</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">Start building your portfolio by adding your first position</p>
            <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md flex items-center mx-auto">
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Position
            </button>
          </div>
        </div>
      )}

      {/* Performance Metrics */}
      {performance && performance.metrics && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Performance Metrics</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatPercentage(performance.metrics.totalReturn || 0)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Total Return</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatPercentage(performance.metrics.annualizedReturn || 0)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Annualized</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatPercentage(performance.metrics.volatility || 0)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Volatility</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {(performance.metrics.sharpeRatio || 0).toFixed(2)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Sharpe Ratio</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatPercentage(performance.metrics.maxDrawdown || 0)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Max Drawdown</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatPercentage(performance.metrics.winRate || 0)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Win Rate</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioPage; 